import React, { useCallback, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import Button from "../../../common/buttons/Button";
import {
  runCheckoutWidget,
  WIDGET_ERRORS,
} from "../payments/zohoCheckout";
import {
  checkoutPlan,
  getCreditOrder,
  getRenewalPlans,
  verifyCreditOrder,
} from "../../../../api/settings/Credits/credits";
import { updateSubscriptionState } from "../../../../store/slice/auth-slice";
import { refreshProfile } from "../../../../utils/refreshProfile";
import PaymentSuccess from "../payments/PaymentSuccess";

const POLL_MS = 4000;

const money = (n) =>
  `₹${(Number(n) || 0).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
const rupees = (n) => `₹${(Number(n) || 0).toLocaleString("en-IN")}`;
const count = (n) => (Number(n) || 0).toLocaleString("en-IN");

const longDate = (value) => {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString("en-IN", {
    day: "numeric",
    month: "short",
    year: "numeric",
    timeZone: "UTC",
  });
};

const numericDate = (value) => {
  if (!value) return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  const dd = String(d.getUTCDate()).padStart(2, "0");
  const mm = String(d.getUTCMonth() + 1).padStart(2, "0");
  return `${dd}-${mm}-${d.getUTCFullYear()}`;
};

/**
 * Renew the subscription: pick Yearly or 3 Years for the clinic's plan
 * (Essentials or Pro), pay in the Zoho checkout, and the server extends the
 * subscription as soon as the payment is confirmed.
 */
const RenewPlan = ({ onRenewed, onClose }) => {
  const dispatch = useDispatch();

  const [data, setData] = useState(null);
  const [loadError, setLoadError] = useState("");
  const [term, setTerm] = useState("Yearly");
  // select | starting | paying | confirming | success | failed
  const [phase, setPhase] = useState("select");
  const [order, setOrder] = useState(null);
  const [message, setMessage] = useState("");
  const [checking, setChecking] = useState(false);

  useEffect(() => {
    getRenewalPlans()
      .then((response) => setData(response?.data?.data || null))
      .catch(() =>
        setLoadError("Plans could not be loaded. Please try again.")
      );
  }, []);

  const selected = data?.plans?.find((p) => p.term === term);

  const finish = useCallback(
    (latest) => {
      setOrder(latest);
      setPhase("success");
      dispatch(
        updateSubscriptionState({
          subscription_started_at: numericDate(latest.plan?.startedAt),
          subscription_expires_at: numericDate(latest.plan?.expiresAt),
          subscription_expires_iso: latest.plan?.expiresAt,
          plan_name: latest.plan?.term,
          whatsapp_credit_balance: latest.balances?.whatsapp,
          sms_credit_balance: latest.balances?.sms,
        })
      );
      // The partial update above is instant; this brings everything else
      // (grace/lock state, pending notices) in line with the new plan.
      refreshProfile().catch(() => {});
      if (onRenewed) onRenewed(latest);
    },
    [dispatch, onRenewed]
  );

  const applyResult = (latest) => {
    if (latest?.status === "credited") {
      finish(latest);
      return true;
    }
    if (latest && ["failed", "expired"].includes(latest.status)) {
      setOrder(latest);
      setMessage(latest.statusNote || "This payment did not complete.");
      setPhase("failed");
      return true;
    }
    return false;
  };

  const checkOrder = useCallback(
    async (orderId) => {
      try {
        const response = await getCreditOrder(orderId);
        const latest = response?.data?.data;
        if (!latest) return "pending";
        if (latest.status === "credited") {
          finish(latest);
          return "done";
        }
        if (["failed", "expired"].includes(latest.status)) {
          setOrder(latest);
          setMessage(latest.statusNote || "This payment did not complete.");
          setPhase("failed");
          return "done";
        }
      } catch (e) {
        /* a failed check is not a failed payment — keep waiting */
      }
      return "pending";
    },
    [finish]
  );

  useEffect(() => {
    if (phase !== "confirming" || !order?._id) return undefined;

    let stopped = false;
    let timer = null;
    const loop = async () => {
      if (stopped) return;
      const result = await checkOrder(order._id);
      if (!stopped && result === "pending") timer = setTimeout(loop, POLL_MS);
    };
    loop();

    const checkNow = () => {
      if (stopped || document.visibilityState === "hidden") return;
      clearTimeout(timer);
      loop();
    };
    document.addEventListener("visibilitychange", checkNow);
    window.addEventListener("focus", checkNow);
    return () => {
      stopped = true;
      clearTimeout(timer);
      document.removeEventListener("visibilitychange", checkNow);
      window.removeEventListener("focus", checkNow);
    };
  }, [phase, order?._id, checkOrder]);

  const handlePay = async () => {
    if (!selected) return;
    setMessage("");
    setPhase("starting");

    let created;
    try {
      const response = await checkoutPlan(selected.term);
      created = response?.data?.data;
      setOrder(created);
    } catch (error) {
      setMessage(
        error?.response?.data?.error ||
          "Online payment is not available right now. Please contact support."
      );
      setPhase("select");
      return;
    }

    setPhase("paying");
    let outcome = null;
    try {
      outcome = await runCheckoutWidget(created.widget);
    } catch (error) {
      const closed = error?.code === "widget_closed";
      try {
        const response = await verifyCreditOrder(created._id);
        if (applyResult(response?.data?.data)) return;
      } catch (e) {
        /* fall through */
      }
      setMessage(
        closed
          ? "Payment was cancelled. No money was taken."
          : WIDGET_ERRORS[error?.code] ||
              error?.message ||
              "The payment window could not be opened. Please try again."
      );
      setOrder(null);
      setPhase("select");
      return;
    }

    setPhase("confirming");
    try {
      const response = await verifyCreditOrder(created._id, outcome);
      const latest = response?.data?.data;
      if (!applyResult(latest) && latest?.paymentMismatch) {
        setMessage(
          "We could not confirm a payment for this order. If money was " +
            "debited it will be applied automatically; otherwise please try again."
        );
        setOrder(null);
        setPhase("select");
      }
    } catch (e) {
      /* the waiting screen keeps checking */
    }
  };

  const manualCheck = async () => {
    if (!order?._id) return;
    setChecking(true);
    await checkOrder(order._id);
    setChecking(false);
  };

  /* ------------------------- rendering ------------------------ */
  if (phase === "success" && order) {
    return <PaymentSuccess order={order} onDone={onClose} />;
  }

  if (phase === "paying" || phase === "starting" || phase === "confirming") {
    const confirming = phase === "confirming";
    return (
      <div className="w-full max-w-[560px] mx-auto">
        <div className="flex items-center justify-between rounded-15 bg-secondary px-4 py-3">
          <div>
            <p className="text-bodyBB text-darkgrey">
              {confirming ? "Confirming your payment" : "Complete your payment"}
            </p>
            <p className="text-smallRB text-darkgrey opacity-80">
              {selected?.name} · Secure payment by Zoho Payments
            </p>
          </div>
          {order && (
            <p className="text-heading2B text-darkgrey">{money(order.total)}</p>
          )}
        </div>
        <div className="flex flex-col items-center py-14 text-center">
          <div className="h-10 w-10 animate-spin rounded-full border-4 border-primary border-t-transparent" />
          <p className="mt-3 text-bodyRB text-darkgrey">
            {confirming
              ? "Payment received — confirming with the bank…"
              : phase === "starting"
              ? "Preparing your payment…"
              : "Finish the payment in the secure window."}
          </p>
          {confirming && (
            <>
              <p className="mt-1 text-smallRB text-darkgrey opacity-70 max-w-[360px]">
                This screen updates by itself. You can also close it — your
                subscription is extended automatically once the payment is
                confirmed.
              </p>
              <div className="mt-4 w-[170px]">
                <Button
                  type="secondary"
                  action="button"
                  className="!py-[9px] text-bodySBB"
                  onClick={manualCheck}
                  loading={checking}
                >
                  Check again
                </Button>
              </div>
            </>
          )}
        </div>
      </div>
    );
  }

  if (phase === "failed") {
    return (
      <div className="w-full max-w-[460px] mx-auto text-center py-8">
        <div className="mx-auto mb-3 flex h-16 w-16 items-center justify-center rounded-full bg-danger bg-opacity-20 text-[34px] text-danger">
          !
        </div>
        <h2 className="text-heading2B text-darkgrey">Payment not completed</h2>
        <p className="text-bodyRB text-darkgrey mt-2">{message}</p>
        <div className="mt-6 flex justify-center">
          <div className="w-[180px]">
            <Button
              type="primary"
              action="button"
              onClick={() => {
                setOrder(null);
                setMessage("");
                setPhase("select");
              }}
            >
              Try again
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full max-w-[520px] mx-auto">
      {loadError && (
        <p className="text-bodyRB text-danger text-center mt-3">{loadError}</p>
      )}
      {message && (
        <p className="text-bodyRB text-danger text-center mb-3">{message}</p>
      )}
      {!data && !loadError && (
        <p className="text-bodyRB text-darkgrey text-center py-8">
          Loading plans…
        </p>
      )}

      {data && (
        <>
          <p className="text-bodyBB text-darkgrey text-center">
            Choose your {data.tier} renewal
          </p>
          <div className="mt-4 flex flex-col gap-3">
            {data.plans.map((plan) => {
              const isOn = plan.term === term;
              const discounted = plan.discountPercent > 0;
              return (
                <button
                  key={plan.term}
                  type="button"
                  onClick={() => setTerm(plan.term)}
                  className={`text-left rounded-15 border bg-white p-4 transition ${
                    isOn
                      ? "border-primary ring-2 ring-primary ring-opacity-30"
                      : "border-[#E6EAF0] hover:border-primary"
                  }`}
                >
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex items-center gap-3">
                      <span
                        className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full border-2 ${
                          isOn ? "border-primary" : "border-[#B8C1CC]"
                        }`}
                      >
                        {isOn && (
                          <span className="h-2.5 w-2.5 rounded-full bg-primary" />
                        )}
                      </span>
                      <div>
                        <p className="text-bodyBB text-darkgrey">
                          {plan.years === 1 ? "Yearly" : "3 Years"}
                        </p>
                        {discounted && (
                          <span className="inline-block mt-0.5 rounded-full bg-success bg-opacity-20 px-2 py-0.5 text-smallRB text-success">
                            {plan.discountPercent}% off
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="text-right shrink-0">
                      {discounted && (
                        <p className="text-smallRB text-darkgrey opacity-60 line-through">
                          {rupees(plan.listTotal)}
                        </p>
                      )}
                      <p className="text-heading2B text-darkgrey">
                        {rupees(plan.total)}
                      </p>
                      <p className="text-smallRB text-darkgrey opacity-70">
                        incl. {plan.taxPercentage}% GST
                      </p>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>

          <div className="mt-5 w-full flex justify-center">
            <div className="w-full max-w-[320px]">
              <Button
                type="primary"
                action="button"
                className="text-heading2B !p-2 !h-[50px]"
                onClick={handlePay}
                disabled={!selected}
              >
                {selected
                  ? `Proceed to pay ${rupees(selected.total)}`
                  : "Choose a plan"}
              </Button>
            </div>
          </div>
          <p className="mt-3 text-center text-smallRB text-darkgrey opacity-60">
            Secure payment by Zoho Payments · UPI &amp; Net Banking
          </p>
        </>
      )}
    </div>
  );
};

export default RenewPlan;
